'use client';

import { useUserDetails } from 'auth/mutations/userHooks';
import { Flex, FlexRowSpaceBetween } from 'components/primitives/flex';
import BoatCard from 'components/search/v2/BoatCard/BoatCard';
import { getBoatCardUrl } from 'components/search/v2/utils';
import Button from 'components/ui/Button/Button';
import { getAsString } from 'helpers';
import { mediaMSmMax, mediaMMdMin } from 'helpers/breakpoints';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { BoatLocation, SimilarBoat } from 'swagger/models';
import boatsetterPaths from 'utils/boatsetterPaths';
import { useMessageOwnerTracking } from './events/message-owner-events';
import { useSimilarBoatsQuery } from './hooks';

// Types
interface BoatRecommendationsProps {
  viewConversation: () => void;
  boatLocation: BoatLocation;
}

export const CONTENT = {
  title: 'Message sent!',
  subtitle: 'Reach out to a few more owners or send a booking request, the best boats can book up in no time.',
  dynamicTitle: (firstName: string) => `${firstName}, let's get you on the water!`,
  dynamicSubtitle: (location: string) =>
    `You've already taken the first step - now give yourself the best shot at getting on the water. Send more requests or message multiple owners in ${location} to boost your chances of booking the perfect boat.`,
  sectionTitle: 'Recommended boats',
  buttons: {
    sendMoreRequests: 'Send more requests',
    viewConversation: 'view conversation',
  },
} as const;

export default function BoatRecommendations(props: BoatRecommendationsProps) {
  const { viewConversation, boatLocation } = props;
  const router = useRouter();
  const boatId = getAsString(router.query.id);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollLeftRef = useRef(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const { userDetails } = useUserDetails();

  const { data: similarBoatsData, isLoading } = useSimilarBoatsQuery(boatId);
  const {
    trackClickKeepExploringButton,
    trackShowRecommendedBoats,
    trackScrollRecommendedBoats,
    trackClickRecommendedBoatCard,
  } = useMessageOwnerTracking();

  const parsedBoatLocation = useMemo(
    () => `${boatLocation.city}, ${boatLocation.state}, ${boatLocation.country}`,
    [boatLocation.city, boatLocation.state, boatLocation.country]
  );

  const similarBoats = useMemo(
    () =>
      (similarBoatsData?.data?.data?.filter((boat: SimilarBoat) => boat.boat_public_id !== boatId) as SimilarBoat[]) ||
      [],
    [similarBoatsData, boatId]
  );

  const keepExploring = () => {
    trackClickKeepExploringButton({
      boat_public_id: boatId,
      text: 'Keep Exploring',
    });
    router.push({
      pathname: boatsetterPaths.search,
      query: {
        near: parsedBoatLocation,
      },
    });
  };

  useEffect(() => {
    const boats_display = (similarBoats || [])?.map((boat, index) => ({
      boat_public_id: boat.boat_public_id,
      position: index + 1,
    }));

    trackShowRecommendedBoats({
      boat_public_id: boatId,
      boats_display,
    });
  }, [boatId]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft } = container;
    const scroll_direction = scrollLeft > prevScrollLeftRef.current ? 'right' : 'left';
    prevScrollLeftRef.current = scrollLeft;

    const slides = container.querySelectorAll<HTMLElement>('[data-rec-slide]');
    const containerRect = container.getBoundingClientRect();
    let activeIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - containerRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = i;
      }
    });

    const boats_display = similarBoats.map((boat, index) => ({
      boat_public_id: boat.boat_public_id,
      position: index - activeIndex,
    }));

    trackScrollRecommendedBoats({
      scroll_direction,
      boat_public_id: boatId,
      boats_display,
    });
  }, [similarBoats, boatId]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    let scrollTimeout: ReturnType<typeof setTimeout>;
    const debouncedScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 150);
    };

    container.addEventListener('scroll', debouncedScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', debouncedScroll);
      clearTimeout(scrollTimeout);
    };
  }, [handleScroll]);

  const targetIndexRef = useRef(0);

  const scrollCenterToIndex = useCallback((index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const slide = container.querySelector<HTMLElement>(`[data-rec-slide="${index}"]`);
    if (!slide) return;
    const containerCenter = container.clientWidth / 2;
    const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
    container.scrollTo({
      left: slideCenter - containerCenter,
      behavior: 'smooth',
    });
  }, []);

  const scrollByDirection = useCallback(
    (direction: 'prev' | 'next') => {
      const maxIndex = (similarBoats?.length || 1) - 1;
      const { current } = targetIndexRef;
      const targetIndex = direction === 'next' ? Math.min(current + 1, maxIndex) : Math.max(current - 1, 0);
      if (targetIndex === current) return;
      targetIndexRef.current = targetIndex;
      setCurrentIndex(targetIndex);
      scrollCenterToIndex(targetIndex);
    },
    [similarBoats?.length, scrollCenterToIndex]
  );

  // Sync from swipe/touch gestures only after scroll fully stops
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    const onScrollEnd = () => {
      const slides = container.querySelectorAll<HTMLElement>('[data-rec-slide]');
      const containerCenter = container.scrollLeft + container.clientWidth / 2;
      let closest = 0;
      let closestDist = Infinity;
      slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(slideCenter - containerCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      targetIndexRef.current = closest;
      setCurrentIndex(closest);
    };

    container.addEventListener('scrollend', onScrollEnd, { passive: true });
    return () => container.removeEventListener('scrollend', onScrollEnd);
  }, []);

  const canScrollPrev = currentIndex > 0;
  const canScrollNext = currentIndex < (similarBoats?.length || 0) - 1;

  if (isLoading) return null;

  return (
    <>
      <Header>
        <Title>{CONTENT.dynamicTitle(userDetails.first_name)}</Title>
        <Subtitle>{CONTENT.dynamicSubtitle(parsedBoatLocation)}</Subtitle>
      </Header>

      <Container>
        <SubHeadingWrapper>
          <Flex>
            <SubHeading>{CONTENT.sectionTitle}</SubHeading>
          </Flex>
          <NavButtons>
            <NavButton onClick={() => scrollByDirection('prev')} disabled={!canScrollPrev} aria-label="Previous boats">
              <img src="/images/prev-arrow.svg" alt="" width={8} height={14} />
            </NavButton>
            <NavButton onClick={() => scrollByDirection('next')} disabled={!canScrollNext} aria-label="Next boats">
              <img src="/images/next-arrow.svg" alt="" width={8} height={14} />
            </NavButton>
          </NavButtons>
        </SubHeadingWrapper>

        <CarouselWrapper>
          <ScrollContainer ref={scrollRef}>
            {similarBoats?.map((boat, index) => (
              <Slide key={boat.boat_public_id} data-rec-slide={index}>
                <BoatCard
                  trackClick={() => {
                    trackClickRecommendedBoatCard({
                      boat_public_id: boat.boat_public_id,
                      position: index,
                    });
                  }}
                  boat={boat}
                  index={index}
                  url={getBoatCardUrl({
                    boatId: boat.boat_public_id,
                    pos: index,
                  })}
                />
              </Slide>
            ))}
          </ScrollContainer>
        </CarouselWrapper>
      </Container>

      <ButtonContainer>
        <Button text={CONTENT.buttons.sendMoreRequests} onClick={keepExploring} fullWidth />
        <Button text={CONTENT.buttons.viewConversation} onClick={viewConversation} fullWidth outline />
      </ButtonContainer>
    </>
  );
}

const Container = styled.div`
  margin: 0 auto;
  max-width: 100%;
  position: relative;
`;

const Header = styled.div`
  margin-bottom: 32px;
  text-align: left;
`;

const Title = styled.h2`
  font-weight: 600;
  font-size: 28px;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  text-align: left;
`;

const SubHeading = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const NavButtons = styled(Flex)`
  gap: 25px;

  ${mediaMSmMax} {
    display: none;
  }
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 50%;
  border: 1px solid #eef1f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  left: -75px;
  width: calc(100% + 150px);

  ${mediaMSmMax} {
    left: -1.5em;
    width: calc(100% + 3em);
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Horizontal padding allows first/last slides to reach centered position */
  padding: 10px 10% 40px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div`
  flex-shrink: 0;
  scroll-snap-align: center;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

  /* ~1.2 slides visible (centered) */
  width: 83%;

  /* >=640px: ~1.4 slides visible */
  @media ${mediaMMdMin} {
    width: 71%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SubHeadingWrapper = styled(FlexRowSpaceBetween)`
  margin-bottom: 20px;
`;
